import React, { useState, useEffect } from 'react';
/* Fix: Removed non-existent ScoringThresholds and unused imports (ScoringWeights, IntegrityRules, AnalysisResult) */
import { GUITAR_LEVELS } from '../types';
import { useGuitarMatch } from '../context/GuitarMatchContext';
import { GLOBAL_SEVERITY_MATRIX } from '../services/severityEngine';

interface ScoringGuideProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: 'en' | 'fr';
  currentLevelId?: number;
}

export const ScoringGuide: React.FC<ScoringGuideProps> = ({ isOpen, onClose, lang = 'en', currentLevelId: propLevelId }) => {
  const { currentLevelId: contextLevelId, setCoachLevel, result } = useGuitarMatch();
  
  // On utilise la valeur du contexte en priorité, ou la prop si fournie
  const activeLevelId = propLevelId !== undefined ? propLevelId : contextLevelId;
  
  const [selectedLevelId, setSelectedLevelId] = useState(activeLevelId);
  const [isApplying, setIsApplying] = useState(false);

  // Sync state when opening
  useEffect(() => {
    if (isOpen) setSelectedLevelId(activeLevelId);
  }, [isOpen, activeLevelId]);

  if (!isOpen) return null;

  const profile = GLOBAL_SEVERITY_MATRIX[selectedLevelId] || GLOBAL_SEVERITY_MATRIX[4];
  const usedMetrics = result?.audit_trail?.metrics_used as string[] | undefined; // Axe 2: Explicitly type as string[]

  const handleApply = () => {
      setIsApplying(true);
      
      // Appel de la fonction atomique du contexte
      setCoachLevel(selectedLevelId);
      
      // Feedback UI avant fermeture
      setTimeout(() => {
          setIsApplying(false);
          onClose();
      }, 400);
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" onClick={onClose}></div>
      <div className="relative bg-[#020d11] text-white rounded-[60px] max-w-3xl w-full p-8 md:p-12 shadow-2xl border border-white/10 animate-fade-in-up flex flex-col max-h-[90vh] overflow-hidden">
        <header className="flex justify-between items-center mb-8 border-b border-white/5 pb-6 shrink-0">
            <h2 className="text-3xl font-black uppercase tracking-tighter italic">Coach <span className="text-smart-accent">Exigence</span></h2>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all">✕</button>
        </header>

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
            {/* SELECTEUR NIVEAU */}
            <div className="grid grid-cols-4 lg:grid-cols-8 gap-2 mb-8">
                {GUITAR_LEVELS.map(level => (
                    <button 
                        key={level.id} 
                        onClick={() => setSelectedLevelId(level.id)} 
                        className={`py-4 rounded-2xl text-[9px] font-black uppercase transition-all flex flex-col items-center gap-1 border-2 ${selectedLevelId === level.id ? 'bg-white text-black border-white scale-105 shadow-xl' : 'bg-white/5 text-slate-500 border-transparent hover:bg-white/10'}`}
                    >
                        <span>L{level.id}</span>
                        <div className={`h-1 w-4 rounded-full ${selectedLevelId === level.id ? 'bg-smart-accent' : 'bg-white/10'}`} />
                    </button>
                ))}
            </div>

            {/* DASHBOARD CONFIG ACTUELLE */}
            <div className="bg-black/60 p-8 rounded-[40px] border border-white/5 space-y-6 shadow-inner">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-black italic uppercase tracking-tighter text-white">{profile.label}</h3>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Profil de Sévérité L{selectedLevelId}</p>
                    </div>
                    {selectedLevelId === activeLevelId && (
                        <div className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30 text-[9px] font-black uppercase tracking-widest">
                            Config Actuelle
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(!usedMetrics || usedMetrics.includes('onset_detection')) && (
                        <div className="bg-white/[0.03] p-5 rounded-3xl border border-white/5 flex flex-col gap-2">
                            <div className="text-[9px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                                <span>⏱️</span> Rythme
                            </div>
                            <div className="text-2xl font-black text-white italic">±{profile.timing.toleranceMsBase}ms</div>
                            <p className="text-[8px] text-slate-500 uppercase font-medium">
                                {profile.timing.allowTrends ? "Rush/Drag Strict" : "Tolérance Large"}
                            </p>
                        </div>
                    )}

                    {(!usedMetrics || usedMetrics.includes('transient_slope')) && (
                        <div className="bg-white/[0.03] p-5 rounded-3xl border border-white/5 flex flex-col gap-2">
                            <div className="text-[9px] font-black text-cyan-400 uppercase tracking-widest flex items-center gap-2">
                                <span>⚡</span> Attaque
                            </div>
                            <div className="text-lg font-black text-white italic truncate">{profile.articulation.description}</div>
                            <p className="text-[8px] text-slate-500 uppercase font-medium">
                                Target: {(profile.articulation.minTransientRatio * 100).toFixed(0)}%
                            </p>
                        </div>
                    )}

                    {(!usedMetrics || usedMetrics.includes('rms_db')) && (
                        <div className="bg-white/[0.03] p-5 rounded-3xl border border-white/5 flex flex-col gap-2">
                            <div className="text-[9px] font-black text-red-400 uppercase tracking-widest flex items-center gap-2">
                                <span>🔇</span> Propreté
                            </div>
                            <div className="text-2xl font-black text-white italic">&lt; {profile.cleanliness.maxGhostsPerMin}/min</div>
                            <p className="text-[8px] text-slate-500 uppercase font-medium truncate">
                                {profile.cleanliness.description}
                            </p>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-smart-gold/5 border border-smart-gold/20 rounded-2xl flex items-center gap-4">
                    <div className="text-xl">🔒</div>
                    <p className="text-[10px] text-slate-300 italic leading-relaxed">
                        En confirmant, l'IA recalcule votre score instantanément selon ces critères. Ce réglage sera conservé pour vos prochaines prises.
                    </p>
                </div>
            </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 shrink-0 flex gap-4">
            <button 
                onClick={onClose}
                className="flex-1 py-6 rounded-full bg-white/5 text-slate-400 font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all"
            >
                Annuler
            </button>
            <button 
                onClick={handleApply}
                disabled={isApplying}
                className={`flex-[2] py-6 rounded-full font-black uppercase text-xs tracking-[0.4em] shadow-2xl transition-all flex items-center justify-center gap-3 ${isApplying ? 'bg-slate-800 text-slate-500 cursor-not-allowed' : 'bg-smart-accent text-white hover:scale-[1.02] active:scale-95'}`}
            >
                {isApplying ? 'VERROUILLAGE...' : `ACTIVER LE NIVEAU L${selectedLevelId} ⚡`}
            </button>
        </div>
      </div>
    </div>
  );
};